import Card from "@mui/material/Card";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import PaginationBar from "components/PaginationBar/PaginationBar";
import ArgonInput from "components/ArgonInput";

import PaymentOrderHistoryTableData from "Views/FinanceManagment/PaymentOrderHistory/data/PaymentOrderHistoryTableData";

const statusByTabLabel = {
  All: "",
  Started: "CREATED",
  Processing: "AUTHORIZED",
  Paid: "CAPTURED",
  Failed: "FAILED",
  Cancelled: "CANCELLED",
};

const tabLabelByStatus = {
  CREATED: "Started",
  AUTHORIZED: "Processing",
  CAPTURED: "Paid",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

function PaymentOrderHistory() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
  });

  const { columns, rows, loading, pagination, counts } = PaymentOrderHistoryTableData(filters);

  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
  const rowsWithCorrectSno = rows.map((row, index) => ({
    ...row,
    sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {startIndex + index + 1}
      </ArgonTypography>
    ),
  }));

  useEffect(() => {
    setFilters({
      fromDate,
      toDate,
      status,
      searchQuery,
      page: currentPage,
      limit: rowsPerPage,
    });
  }, [fromDate, toDate, status, searchQuery, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, status, searchQuery]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFromDate(null);
    setToDate(null);
    setStatus("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const tabsData = [
    { label: "All", count: counts.all, content: "" },
    { label: "Started", count: counts.started, content: "" },
    { label: "Processing", count: counts.processing, content: "" },
    { label: "Paid", count: counts.paid, content: "" },
    { label: "Failed", count: counts.failed, content: "" },
    { label: "Cancelled", count: counts.cancelled, content: "" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Online Payment Tracking
              </ArgonTypography>

              <ArgonBox
                display="flex"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    width={160}
                    maxDate={dayjs()}
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    width={160}
                    maxDate={dayjs()}
                    minDate={fromDate}
                  />
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleResetFilters}
                    disabled={!fromDate && !toDate && !status && !searchQuery}
                  >
                    Reset
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
                mt={1}
              >
                <ArgonBox
                  sx={{
                    flex: "1 1 620px",
                    minWidth: 0,
                    "& > div": {
                      width: "100%",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{
                      backgroundColor: "#f8fafc",
                      borderRadius: "12px",
                      px: 1,
                      py: 0.5,
                      minHeight: 46,
                    }}
                  >
                    {tabsData.map((tab) => {
                      const tabStatus = statusByTabLabel[tab.label] || "";
                      const selected = (tabLabelByStatus[status] || "All") === tab.label;

                      return (
                        <ArgonBox
                          key={tab.label}
                          component="button"
                          type="button"
                          onClick={() => setStatus(tabStatus)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            height: 34,
                            border: 0,
                            borderRadius: "10px",
                            px: 1.5,
                            backgroundColor: selected ? "#ffffff" : "transparent",
                            boxShadow: selected ? "0 6px 14px rgba(17, 24, 39, 0.08)" : "none",
                            color: "#5f6f8f",
                            cursor: "pointer",
                            font: "inherit",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <ArgonTypography
                            variant="caption"
                            fontWeight="medium"
                            color="text"
                            sx={{ fontSize: "0.82rem", lineHeight: 1 }}
                          >
                            {tab.label}
                          </ArgonTypography>
                          <ArgonBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              width: 21,
                              height: 21,
                              borderRadius: "50%",
                              backgroundColor: "#3396D3",
                              color: "#ffffff",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {tab.count}
                          </ArgonBox>
                        </ArgonBox>
                      );
                    })}
                  </ArgonBox>
                </ArgonBox>
                <ArgonBox
                  sx={{
                    width: { xs: "100%", md: 245 },
                    alignSelf: "center",
                    "& .MuiInputBase-root": {
                      height: 42,
                    },
                  }}
                >
                  <ArgonInput
                    placeholder="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2">Loading...</ArgonTypography>
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Payment found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rowsWithCorrectSno} />
              )}

              {pagination.totalItems > 0 && (
                <PaginationBar
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  totalItems={pagination.totalItems}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PaymentOrderHistory;
